import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivate, Router, UrlTree } from '@angular/router';
import { Observable, catchError, map, of } from 'rxjs';
import { AuthenticationService } from '../services/authentication.service';
import { RolName, SoporteArea } from '../interfaces/admin.users.interface';

/**
 * Restringe una ruta a los roles que tienen permiso sobre el área indicada en
 * `data: { area: 'GERENCIA' }`, usando la misma regla que el home
 * (AuthenticationService.getAllowerAreas).
 *
 * Es solo una validación de interfaz: el rol vive en localStorage y puede
 * editarse, por lo que el backend debe validar el permiso de verdad.
 */
@Injectable({ providedIn: 'root' })
export class AreaRoleGuard implements CanActivate {
  constructor(private router: Router, private authService: AuthenticationService) {}

  canActivate(route: ActivatedRouteSnapshot): Observable<boolean | UrlTree> {
    const area = route.data['area'] as SoporteArea | undefined;
    const denied = this.router.createUrlTree(['/home']);

    if (!area) {
      return of(denied);
    }

    const storedRole = this.authService.getStoredRole();
    if (storedRole) {
      return of(this.isAllowed(storedRole, area) ? true : denied);
    }

    // Sesión sin rol guardado (p. ej. abierta antes de este cambio): se consulta una vez.
    const username = this.authService.getStoredUsername();
    if (!username) {
      return of(this.router.createUrlTree(['/auth/login']));
    }

    return this.authService.getUserRole(username).pipe(
      map((rol) => (this.isAllowed(rol, area) ? true : denied)),
      catchError(() => of(denied))
    );
  }

  private isAllowed(rol: RolName, area: SoporteArea): boolean {
    return this.authService.getAllowerAreas(rol).includes(area);
  }
}
